import { CV } from "@easycv/common/types/cv";
import { EffortObject, TimeObject } from "@easycv/common/types/util";
import {
  getPeriodFromDateObject,
  getStringFromDateObject,
  isTimeWithinRange
} from "../../../lib/dateHelpers.js";
import { getEffortText } from "../../../lib/formatUtilities.js";
import { CCInteractiveTable } from "../../cclib/CCInteractiveTable";
import { CCSetup } from "../../cclib/CCSetup";
import {
  CCInteractiveTableColumn,
  CCInteractiveTableOptions,
  InferedColumnType,
  InferedRowType,
  TableDataRow
} from "../../cclib/types/table";
import { afcsDataPrefix, SECTIONS_WITH_COUNTS } from "./afcs";

const defaultOptions = {
  editable: {
    allowAddingRows: false,
    allowEditingText: true,
    allowRemovingRows: true,
    disableEverything: false
  },
  widthOverride: "1024px"
};
const publicationOptions = {
  editable: {
    allowAddingRows: false,
    allowEditingText: false,
    allowRemovingRows: true,
    disableEverything: false
  },
  widthOverride: "1024px"
};
const bibBookishColumns = [
  { width: "13%", display: "Date", db: "time" as const },
  { width: "50%", display: "Citation", db: "line" as const },
  { width: "23%", display: "Special Comments", db: "comments" as const },
  { width: "7%", display: "In Press", db: "inPress" as const },
  { width: "7%", display: "Submitted", db: "submitted" as const },
  { display: "", hidden: true, db: "id" as const }
];
const bibPeerishColumns = [
  { width: "8%", display: "Date", db: "time" as const },
  { width: "50%", display: "Citation", db: "line" as const },
  { width: "17%", display: "Special Comments", db: "comments" as const },
  { width: "7%", display: "PubMed ID", db: "PMID" as const },
  { width: "5%", display: "In Press", db: "inPress" as const },
  { width: "8%", display: "Author Place", db: "authorPlace" as const },
  { width: "5%", display: "Submitted", db: "submitted" as const },
  { display: "", hidden: true, db: "id" as const }
];
const presentationsColumns = [
  { width: "10%", display: "Date", db: "time" as const },
  { width: "30%", display: "Title", db: "title" as const },
  { width: "20%", display: "Location", db: "location" as const },
  { width: "8%", display: "Event", db: "event" as const },
  {
    width: "8%",
    display: "Organization/Institution",
    db: "organization" as const
  },
  { width: "8%", display: "Grand Rounds", db: "presType" as const },
  { width: "8%", display: "Didactic/Invited Pres", db: "didactic" as const },
  { width: "8%", display: "Author", db: "isAuthor" as const },
  { display: "", hidden: true, db: "id" as const }
];
const emCommitteesColumns = [
  { width: "20%", display: "Date", db: "time" as const },
  {
    width: "70%",
    display: "Name of Committee or Responsibility",
    db: "title" as const
  },
  { width: "10%", display: "Leadership Position", db: "isLeadership" as const },
  { display: "", hidden: true, db: "id" as const }
];

const unlockedCellIsSelect = (options: string[]) => {
  return <T>(data: T, locked: boolean, styling: string, cc: CCSetup) => {
    if (locked) return undefined;
    return (
      `<td style="${styling}"><select class="form-control confSelect" ${!cc.checkPermissions().writableAsMenteeIdentity() ? `disabled=""` : ""}>` +
      '<option value="">Choose</option>' +
      options
        .map((opt) => {
          return `<option ${opt == data ? "selected " : ""}value="${opt}">${opt}</option>`;
        })
        .join("") +
      `</select></td>`
    );
  };
};
const convertKebabKey = (key: string) => {
  if (key.startsWith("custom-")) key = key.substring("custom-".length);
  return key.split("-").reduce((acc, curr, i) => {
    if (i === 0) return curr;
    return acc + curr.charAt(0).toUpperCase() + curr.substring(1);
  }, "");
};
const genericFilter = (cc: CCSetup, k?: string, v?: unknown) => {
  if (k != undefined && v != undefined) {
    const time = v["time"] ?? v["date"] ?? v["period"];
    if (time != undefined) {
      return isTimeWithinRange(
        {
          ...time,
          end: time.end ?? time.start
        },
        cc.startDate,
        cc.endDate
      );
    }
  }
  return false;
};
const objFilter = (
  obj: object,
  callback: (k: string, v: unknown) => boolean,
  cc: CCSetup
) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([k, v]) => {
      return genericFilter(cc, k, v) && callback(k, v);
    })
  );
};

type ColumnTypes = {
  [K in keyof typeof mapping]: InferedColumnType<
    (typeof mapping)[K]["columns"]
  >;
};
type RowTypes = {
  [K in keyof typeof mapping]: InferedRowType<(typeof mapping)[K]["columns"]>;
};
type MappingType = {
  [key: string]: {
    options: CCInteractiveTableOptions;
    columns: CCInteractiveTableColumn[];
    override?: Record<string, () => string>;
    transform?: Record<string, (_?: unknown) => string>;
    data: (cv: CV, cc: CCSetup) => Record<string, any>;
  };
};
const mapping: MappingType = {
  "custom-courses": {
    options: {
      ...defaultOptions,
      lockColumns: ["time", "title", "effort", "location"],
      cellCreator: unlockedCellIsSelect([
        "Course director",
        "Course co-director",
        "Primary lecturer"
      ])
    },
    columns: [
      { width: "10%", display: "Date", db: "time" as const },
      { display: "Title", db: "title" as const },
      { width: "10%", display: "Level of Effort", db: "effort" as const },
      { display: "Location", db: "location" as const },
      { display: "Role", db: "role" as const },
      { display: "", hidden: true, db: "id" as const }
    ],
    override: {
      role: () => ""
    },
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.teaching ?? {},
        (_, v) => {
          return v["type"] === "Teaching of Students in Courses";
        },
        cc
      )
  },
  "custom-grants": {
    options: {
      ...defaultOptions,
      lockColumns: [
        "time",
        "title",
        "fundingSource",
        "role",
        "piName",
        "dateComment",
        "type"
      ],
      cellCreator: unlockedCellIsSelect(["Yes", "No"])
    },
    columns: [
      { width: "7%", display: "Date", db: "time" as const },
      { display: "Title", db: "title" as const },
      { display: "Funder", db: "fundingSource" as const },
      { display: "Role on Project", db: "role" as const },
      { width: "10%", display: "PI Name", db: "piName" as const },
      { display: "Date Comment", db: "dateComment" as const },
      { width: "7%", display: "Type", db: "type" as const },
      { width: "7%", display: "Awarded?", db: "awarded" as const },
      { display: "", hidden: true, db: "id" as const }
    ],
    override: {
      awarded: () => ""
    },
    transform: {
      type: (t?: string) => (t ? t.charAt(0).toUpperCase() + t.substring(1) : t)
    },
    data: (cv: CV, cc: CCSetup) =>
      objFilter(cv.lists?.projects ?? {}, () => true, cc)
  },
  "custom-peer-original": {
    options: publicationOptions,
    columns: bibPeerishColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.scholarship ?? {},
        (_, v) => v["type"] === "Research Investigations",
        cc
      )
  },

  "custom-peer-other": {
    options: publicationOptions,
    columns: bibPeerishColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.scholarship ?? {},
        (_, v) => v["type"] === "otherPeer",
        cc
      )
  },
  "custom-peer-articles": {
    options: publicationOptions,
    columns: bibPeerishColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.scholarship ?? {},
        (_, v) => v["type"] === "reviewArticle",
        cc
      )
  },
  "custom-abstracts": {
    options: publicationOptions,
    columns: bibPeerishColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.scholarship ?? {},
        (_, v) => v["type"] === "abstracts",
        cc
      )
  },
  "custom-chapters": {
    options: publicationOptions,
    columns: bibBookishColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.scholarship ?? {},
        (_, v) => v["type"] === "chapters",
        cc
      )
  },
  "custom-books": {
    options: publicationOptions,
    columns: bibBookishColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.scholarship ?? {},
        (_, v) => v["type"] === "books",
        cc
      )
  },
  "custom-book-reviews": {
    options: publicationOptions,
    columns: bibBookishColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.scholarship ?? {},
        (_, v) => v["type"] === "reviews",
        cc
      )
  },
  "custom-digital-pub": {
    options: publicationOptions,
    columns: [
      { width: "10%", display: "Date", db: "time" as const },
      { width: "50%", display: "Citation", db: "line" as const },
      { width: "40%", display: "Special Comments", db: "comments" as const },
      { display: "", hidden: true, db: "id" as const }
    ],
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.scholarship ?? {},
        (_, v) => v["type"] === "nonPrint",
        cc
      )
  },
  "custom-pres-local": {
    options: publicationOptions,
    columns: presentationsColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.presentations ?? {},
        (_, v) => v["type"] === "local",
        cc
      )
  },
  "custom-pres-regional": {
    options: publicationOptions,
    columns: presentationsColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.presentations ?? {},
        (_, v) => v["type"] === "regional",
        cc
      )
  },
  "custom-pres-national": {
    options: publicationOptions,
    columns: presentationsColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.presentations ?? {},
        (_, v) => v["type"] === "national",
        cc
      )
  },
  "custom-pres-international": {
    options: publicationOptions,
    columns: presentationsColumns,
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.presentations ?? {},
        (_, v) => v["type"] === "international",
        cc
      )
  },
  "custom-committee-not": {
    options: publicationOptions,
    columns: emCommitteesColumns,
    transform: {
      isLeadership: (isLeadership?: boolean) => (!isLeadership ? "" : "Yes")
    },
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.committeeService ?? {},
        (_, v) =>
          v["type"] === "public" &&
          (v["committeeType"] ?? "Not Selected") === "Not Selected",
        cc
      )
  },
  "custom-committee-local": {
    options: publicationOptions,
    columns: emCommitteesColumns,
    transform: {
      isLeadership: (isLeadership?: boolean) => (!isLeadership ? "" : "Yes")
    },
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.committeeService ?? {},
        (_, v) =>
          v["type"] === "public" &&
          v["committeeType"]?.toLowerCase() === "local",
        cc
      )
  },
  "custom-committee-regional": {
    options: publicationOptions,
    columns: emCommitteesColumns,
    transform: {
      isLeadership: (isLeadership?: boolean) => (!isLeadership ? "" : "Yes")
    },
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.committeeService ?? {},
        (_, v) =>
          v["type"] === "public" &&
          v["committeeType"]?.toLowerCase() === "regional",
        cc
      )
  },
  "custom-committee-national": {
    options: publicationOptions,
    columns: emCommitteesColumns,
    transform: {
      isLeadership: (isLeadership?: boolean) => (!isLeadership ? "" : "Yes")
    },
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.committeeService ?? {},
        (_, v) =>
          v["type"] === "public" &&
          v["committeeType"]?.toLowerCase() === "national",
        cc
      )
  },
  "custom-committee-international": {
    options: publicationOptions,
    columns: emCommitteesColumns,
    transform: {
      isLeadership: (isLeadership?: boolean) => (!isLeadership ? "" : "Yes")
    },
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.committeeService ?? {},
        (_, v) =>
          v["type"] === "public" &&
          v["committeeType"]?.toLowerCase() === "international",
        cc
      )
  },
  "custom-committee-uni": {
    options: publicationOptions,
    columns: [
      { width: "20%", display: "Date", db: "time" as const },
      {
        width: "40%",
        display: "Name of Committee or Responsibility",
        db: "title" as const
      },
      {
        width: "40%",
        display: "Institution/Organization",
        db: "location" as const
      },
      { display: "", hidden: true, db: "id" as const }
    ],
    data: (cv: CV, cc: CCSetup) =>
      objFilter(
        cv.lists?.committeeService ?? {},
        (_, v) => v["type"] === "institutional",
        cc
      )
  }
};
export { mapping as StanfordEM_Tables };

const isTimeObject = (value: unknown): value is TimeObject => {
  return typeof value === "object" && "start" in value;
};
const isEffortObject = (value: unknown): value is EffortObject => {
  return (
    typeof value === "object" &&
    "num" in value &&
    "nominator" in value &&
    "per" in value
  );
};
const resolveData = <S extends keyof typeof mapping>(
  map: (typeof mapping)[S],
  column: string,
  value: unknown,
  item: Record<string, unknown>
): string | undefined => {
  if (map.override?.[column] != undefined) {
    return map.override[column]();
  }
  if (map.transform?.[column] != undefined) {
    return map.transform[column](value);
  }
  if (value === null || value == undefined) return "";
  if (typeof value === "string") {
    return value;
  } else if (typeof value === "boolean") {
    return value ? "Yes" : "";
  } else if (isTimeObject(value)) {
    if (
      "end" in value &&
      typeof value.end === "object" &&
      value.end != null &&
      (value.end.year ?? value.end.month ?? value.end.date) == undefined
    ) {
      return getStringFromDateObject(value);
    }
    return getPeriodFromDateObject(value);
  } else if (isEffortObject(value)) {
    return getEffortText(item, { noComment: true });
  }
};
export function genericTablePopulate<S extends keyof typeof mapping>(
  table: CCInteractiveTable<ColumnTypes[S]>,
  cc: CCSetup
) {
  const rows: Array<RowTypes[S]> = [];
  const map: (typeof mapping)[S] = mapping[table.dataKey];
  const columns = map.columns.map(({ db }) => db);
  const data = map.data(cc.newDef, cc);
  for (const key of Object.keys(data)) {
    rows.push(
      Object.fromEntries(
        columns.map((col) => [
          col,
          resolveData(map, col, data[key][col], data[key])
        ])
      )
    );
  }
  if (columns.includes("id")) {
    table.updateRows(
      (oldRow) => {
        for (let i = 0; i < rows.length; ++i) {
          if (rows[i]["id"] == oldRow["id"]) {
            const overrides = Object.keys(map.override ?? {});
            const override = overrides.reduce((acc, curr) => {
              return {
                ...acc,
                [curr]: oldRow[curr]
              };
            }, {});
            return { ...rows.splice(i, 1)[0], ...override };
          }
        }
      },
      () => rows
    );
  }
}
export function genericTableUpdate(table: CCInteractiveTable, cc: CCSetup) {
  if (!cc.checkPermissions().writableAsMenteeIdentity()) {
    table.disableEditing();
  } else {
    table.enableEditing();
  }
  table.populateFromDatabase();
}
export function genericTableSetup(container: JQuery, cc: CCSetup) {
  const key = container.data("key");

  let singleTableKey = (() => {
    if (key === "custom-courses") {
      return "courses_jump_binding";
    } else if (key === "custom-grants") {
      return "grants_jump_binding";
    }
    return undefined;
  })();
  if (singleTableKey != undefined) {
    cc.addButton(
      "primary",
      `<span>Sync table with CV</span>`,
      { relative: "after", selector: `[data-key="${singleTableKey}"]` },
      "syncSection",
      () => {
        if (!cc.checkPermissions().writableAsMenteeIdentity()) {
          return;
        }
        genericTablePopulate(table, cc);
        cc.update(cc.struct.structure);
      }
    );
  }
  const baseClass = convertKebabKey(key) + "Table";
  const table = new CCInteractiveTable(
    container,
    cc,
    baseClass,
    afcsDataPrefix,
    key,
    mapping[key].columns,
    mapping[key].options
  );
  if (SECTIONS_WITH_COUNTS.includes(key) && typeof key === "string") {
    const headerDataKey =
      key.substring("custom-".length).replace(/-/g, "_") + "_jump_binding";
    $(`[data-key="${headerDataKey}"]`).append(
      `<span data-key="${key}-count"></span>`
    );
    table.on("rowCountChange", () => {
      $(`[data-key="${table.dataKey}-count"]`).text(` (${table.rows.length})`);
    });
    table.on("dataRefresh", () => {
      $(`[data-key="${table.dataKey}-count"]`).text(` (${table.rows.length})`);
    });
    table.on("edit", () => {
      cc.update(
        cc.struct.structure.filter(
          (v) =>
            v.type === "custom" &&
            ["custom-courses", "custom-grants"].includes(v.key)
        )
      );
    });
  }
  return table;
}

const presSummaryColumns = [
  { display: "", db: "category" as const },
  { display: "Local", db: "local" as const },
  { display: "Regional", db: "regional" as const },
  { display: "National", db: "national" as const },
  { display: "International", db: "international" as const }
];
type PresSummaryColumnsT = InferedColumnType<typeof presSummaryColumns>;

export function presSummaryUpdate(
  table: CCInteractiveTable<PresSummaryColumnsT>,
  cc: CCSetup
) {
  const colsCounts = { local: 0, regional: 0, national: 0, international: 0 };
  const rowCounts = {
    "Not Selected": { ...colsCounts },
    Abstract: { ...colsCounts },
    "Didactic Pres": { ...colsCounts },
    "Invited Pres": { ...colsCounts },
    "Research Abstract Pres": { ...colsCounts },
    "Panel Pres": { ...colsCounts },
    Research: { ...colsCounts },
    "Serving as a Mentor": { ...colsCounts },
    "Advisor for Research Abstract Pres": { ...colsCounts }
  };
  const allTables = cc.allCustomSectionData() as CCInteractiveTable[];
  const presTables = allTables.filter((t) =>
    t?.columns.some(({ db }) => db === "didactic")
  );
  const allRows = presTables.flatMap((t) => {
    return t.rows.map((row) => ({
      ...(row as TableDataRow<typeof presentationsColumns>),
      __type_key: t.dataKey.split("-").pop()
    }));
  });
  const counts: Record<
    keyof typeof rowCounts,
    Record<keyof typeof colsCounts, number>
  > = allRows.reduce((acc, curr) => {
    (acc[curr?.didactic?.replace(/Presentation(s)?/, "Pres")] ??
      acc["Not Selected"])[curr.__type_key]++;
    return acc;
  }, rowCounts);
  const summaryRows: InferedRowType<typeof presSummaryColumns>[] = [];
  for (const [category, data] of Object.entries(counts)) {
    summaryRows.push({
      category,
      local: String(data.local),
      regional: String(data.regional),
      national: String(data.national),
      international: String(data.international)
    });
  }
  table.clear();
  table.bulkAddRowsFromData(summaryRows, false);
}
export function presSummarySetup(container: JQuery, cc: CCSetup) {
  const table = new CCInteractiveTable(
    container,
    cc,
    "presSummaryTable",
    afcsDataPrefix,
    "custom-pres-summary",
    presSummaryColumns,
    {
      editable: {
        allowAddingRows: false,
        allowEditingText: false,
        allowRemovingRows: false,
        disableEverything: false
      },
      widthOverride: "1024px"
    }
  );
  const allTables = cc.allCustomSectionData() as CCInteractiveTable[];
  const presTables = allTables.filter((t) =>
    t?.columns.some(({ db }) => db === "didactic")
  ) as CCInteractiveTable<PresSummaryColumnsT>[];
  for (const presTable of presTables) {
    presTable
      .off("rowCountChange", () => {})
      .on("rowCountChange", () => {
        presSummaryUpdate(table, cc);
      });
  }
  presSummaryUpdate(table, cc);
  return table;
}

export const reputationColumns = [
  { width: "5%", display: "", db: "_number" as const },
  { display: "First Name", db: "first_name" as const },
  { display: "Last Name", db: "last_name" as const },
  { display: "Email Address", db: "email" as const },
  { display: "Institution", db: "institution" as const }
];
type ReputationColumnsT = InferedColumnType<typeof reputationColumns>;
export function reputationUpdate(
  table: CCInteractiveTable<ReputationColumnsT>,
  cc: CCSetup,
  context: "mentee" | "mentor" = "mentee"
) {
  if (cc.uninitializedYearState) {
    table.populateFromDatabase();
  }
  if (
    (cc.checkPermissions().writableAsMenteeIdentity() &&
      context === "mentee") ||
    (cc.checkPermissions().writableAsMentorIdentity() && context === "mentor")
  ) {
    table.enableEditing();
    $(`#${table.baseClass}Body`)
      .children("tr")
      .children("td")
      .attr("contenteditable", "");
  } else {
    table.disableEditing();
    $(`#${table.baseClass}Body`)
      .children("tr")
      .children("td")
      .removeAttr("contenteditable");
  }
  const rows = table.rows.filter(Boolean);
  if (rows.length < 4) {
    table.clear();
    const resetRows = [];
    for (let i = 0; i < 4; ++i) {
      resetRows.push({ _number: i + 1 });
    }
    table.bulkAddRowsFromData(resetRows, false);
  }
}
export function reputationSetup(
  container: JQuery,
  cc: CCSetup,
  editable: boolean = true
) {
  const table = new CCInteractiveTable(
    container,
    cc,
    "reputationTable",
    afcsDataPrefix,
    "custom-reputation",
    reputationColumns,
    {
      editable: {
        allowAddingRows: false,
        allowEditingText: editable,
        allowRemovingRows: false,
        disableEverything: false
      },
      lockColumns: ["_number"],
      widthOverride: "1024px"
    }
  );
  // 4 for "list four individuals who can attest to your professional reputation."
  const STANFORD_REPUTATION_ENTRIES = 4;
  if (table.rows.length !== STANFORD_REPUTATION_ENTRIES) {
    table.clear();
    table.bulkAddRowsFromData(
      Array(4)
        .fill(0)
        .map((_, i) => ({
          _number: String(i + 1),
          email: "",
          first_name: "",
          institution: "",
          last_name: ""
        }))
    );
  }
  table.populateFromDatabase();
  table.on("edit", () => {
    cc.update(cc.struct.structure.filter((v) => v.type === "custom"));
  });
  return table;
}
